import { useMemo, useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, WalletCards } from "lucide-react";
import { useContasReceber } from "@/hooks/useContasReceber";
import { usePacientes } from "@/hooks/usePacientes";
import { openWhatsApp, renderTemplate } from "@/lib/whatsapp";
import { parseLocalDate, todayLocalISO } from "@/lib/date-utils";

const money = (value: number) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Cobrancas() {
  const { contasReceber, updateConta } = useContasReceber();
  const { pacientes } = usePacientes();
  const [filter, setFilter] = useState("pendentes");
  const todayIso = todayLocalISO();
  const today = parseLocalDate(todayIso);
  today.setHours(0, 0, 0, 0);

  const cobrancas = useMemo(() => {
    return contasReceber
      .filter((conta) => conta.status === "Pendente" || conta.status === "Vencida")
      .map((conta) => {
        const vencimento = parseLocalDate(conta.data_vencimento);
        vencimento.setHours(0, 0, 0, 0);
        const vencida = vencimento < today;
        return { ...conta, vencida };
      })
      .filter((conta) => {
        if (filter === "vencidas") return conta.vencida;
        if (filter === "hoje") return conta.data_vencimento === todayIso;
        return true;
      });
  }, [contasReceber, filter]);

  const send = (conta: any) => {
    const paciente = pacientes.find((item) => item.id === conta.paciente_id);
    const message = renderTemplate("Olá, {paciente}! Consta um pagamento pendente de {valor}, com vencimento em {vencimento}. Descrição: {descricao}.", {
      paciente: paciente?.nome || "paciente",
      valor: money(Number(conta.valor || 0)),
      vencimento: conta.data_vencimento,
      descricao: conta.descricao,
    });
    openWhatsApp(paciente?.telefone, message);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><WalletCards className="h-7 w-7" /> Cobranças</h1>
            <p className="text-muted-foreground">Acompanhe pendências e envie mensagens de cobrança por WhatsApp.</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full sm:w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pendentes">Pendentes</SelectItem>
              <SelectItem value="vencidas">Vencidas</SelectItem>
              <SelectItem value="hoje">Vencem hoje</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Cobranças</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{cobrancas.length}</p></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Valor aberto</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{money(cobrancas.reduce((sum, conta) => sum + Number(conta.valor || 0), 0))}</p></CardContent></Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Vencidas</CardTitle></CardHeader><CardContent><p className="text-2xl font-bold">{cobrancas.filter((conta) => conta.vencida).length}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader><CardTitle>Lista de cobranças</CardTitle><CardDescription>Use o WhatsApp para contato e marque como recebida quando houver pagamento.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            {cobrancas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma cobrança encontrada.</p>
            ) : cobrancas.map((conta) => {
              const paciente = pacientes.find((item) => item.id === conta.paciente_id);
              return (
                <div key={conta.id} className="flex flex-col gap-3 rounded-md border p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{conta.descricao}</p>
                      <Badge variant={conta.vencida ? "destructive" : "secondary"}>{conta.vencida ? "Vencida" : conta.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{paciente?.nome || "Sem paciente"} - vence em {conta.data_vencimento}</p>
                    <p className="text-lg font-semibold">{money(Number(conta.valor || 0))}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => send(conta)}><MessageCircle className="mr-2 h-4 w-4" />WhatsApp</Button>
                    <Button onClick={() => updateConta({ id: conta.id, status: "Recebida", data_recebimento: todayLocalISO() })}>Marcar recebida</Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
